import { defineMessages, type MessageDescriptor } from 'react-intl';
import { VideoEmbedTypeEnum } from '../../../common/core-api';
import { CATEGORY_DISPLAY_PREFIX, PAGE_DISPLAY_PREFIX } from './button-link-config';
import { type PageCmsPanel, type PageCmsPanels } from './panel-types';
import { parseEmbedCode } from './video-embed';

// Error displays derived from typed input wait for a typing pause instead of reacting per keystroke.
export const VALIDATION_DEBOUNCE_MS = 500;

export const panelValidationMessages = defineMessages({
  displayOptionRequired: {
    id: 'editCustomPagePanelDisplayOptionError',
    defaultMessage: 'Elements should have at least one display option selected'
  },
  embedNotSupported: {
    id: 'embedNotSupported',
    defaultMessage: 'This embed code is not supported.'
  },
  videoLinkUnrecognised: {
    id: 'editCustomPageVideoLinkUnrecognised',
    defaultMessage: 'The video link is unrecognised'
  },
  selectValueRequired: {
    id: 'editCustomPageSelectValueError',
    defaultMessage: 'Select a value'
  },
  imageRequired: {
    id: 'editCustomPageImageRequiredError',
    defaultMessage: 'Upload an image'
  }
});

/*
 * Display-option errors surface only after a save attempt (gated by showPanelErrors),
 * mirroring the page-title validation.
 */
export const getDisplayOptionsError = (config: PageCmsPanel['settings']): MessageDescriptor | null => {
  switch (config.__typename) {
    case 'IdeasPanelSettings':
      return config.ideasDisplay.length === 0 ? panelValidationMessages.displayOptionRequired : null;
    case 'LeaderboardPanelSettings':
      return config.leaderboardDisplay.length === 0 ? panelValidationMessages.displayOptionRequired : null;
    default:
      return null;
  }
};

/*
 * Video errors are "live": they surface as soon as the (debounced) input is invalid and clear as
 * soon as the embed parses, without waiting for a save attempt. Blank input is not an error.
 */
export const getVideoPanelError = (config: PageCmsPanel['settings']): MessageDescriptor | null => {
  if (config.__typename !== 'VideoPanelSettings') {
    return null;
  }
  if (parseEmbedCode(config.videoEmbedCode ?? '')) {
    return null;
  }
  if (config.embed === VideoEmbedTypeEnum.CODE) {
    return config.videoEmbedCode?.trim() ? panelValidationMessages.embedNotSupported : null;
  }
  return config.videoUrl?.trim() ? panelValidationMessages.videoLinkUnrecognised : null;
};

export const getImagePanelError = (config: PageCmsPanel['settings']): MessageDescriptor | null =>
  config.__typename === 'ImagePanelSettings' && !config.imageFile ? panelValidationMessages.imageRequired : null;

/*
 * A bare category_/page_ display prefix means a challenge/page link mode was chosen but no target
 * picked yet. The message renders inside LinkConfigSelect, next to the offending select.
 */
export const getLinkConfigError = (config: PageCmsPanel['settings']): MessageDescriptor | null => {
  if (config.__typename !== 'TextPanelSettings' && config.__typename !== 'ImagePanelSettings') {
    return null;
  }
  const display = config.buttonConfig?.display;
  return display === CATEGORY_DISPLAY_PREFIX || display === PAGE_DISPLAY_PREFIX
    ? panelValidationMessages.selectValueRequired
    : null;
};

// Errors whose message is shown at the top of the panel card (leaderboard-style); video and
// link-config messages render inside their editors instead.
export const getPanelCardError = (config: PageCmsPanel['settings']): MessageDescriptor | null =>
  getDisplayOptionsError(config) ?? getImagePanelError(config);

export const getPanelConfigError = (config: PageCmsPanel['settings']): MessageDescriptor | null =>
  getPanelCardError(config) ?? getVideoPanelError(config) ?? getLinkConfigError(config);

export const panelsHaveErrors = (panels: PageCmsPanels): boolean =>
  panels.some((row) => row.some((panel) => getPanelConfigError(panel.settings) !== null));
